import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import JobEvent from 'jobs/JobEvent'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import { match } from 'utils/pattern-match-util'
import styles from './JobEvent.sass'

interface Props {
  event: JobEvent
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: '2-digit',
})

export default observer(function JobEvent({ event }: Props) {
  const isTaskUpdate =
    event.eventType === 'TASK_MARKED_COMPLETE' ||
    event.eventType === 'TASK_MARKED_INCOMPLETE'

  return (
    <div className={cx(styles.root)} data-test={`job-event-${event.eventType}`}>
      <div className={styles.container}>
        <div className={styles.avatarContainer}>
          <Avatar size={32} subject={event.triggeredBy} />
        </div>
        <div className={styles.content}>
          <EventDescription event={event} />
          <div className={styles.info}>
            <div
              data-test={isTaskUpdate ? 'task-updates' : 'job-updates'}
              className={
                isTaskUpdate ? styles.iconTaskUpdate : styles.iconJobUpdate
              }
            >
              <Icon name={isTaskUpdate ? 'task-updates' : 'job-updates'}></Icon>
            </div>
            <div className={styles.date}>
              {dateFormatter.format(event.dateCreated)}
            </div>
          </div>
        </div>
      </div>
      {event.reason && <div className={styles.reason}>{event.reason}</div>}
    </div>
  )
})

interface EventDescriptionProps {
  event: JobEvent
}

const EventDescription = observer(({ event }: EventDescriptionProps) => {
  return (
    <p className={styles.description}>
      {match(event.eventType, {
        AMENDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> amended the job
          </>
        ),
        CLAIMED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> picked up the job
          </>
        ),
        CLOSED: () => {
          switch (event.jobResolution) {
            case 'DONE':
              return (
                <>
                  <strong>{event.triggeredBy.displayName}</strong> closed the
                  job as Completed
                </>
              )
            case 'CANCELLED':
              return (
                <>
                  <strong>{event.triggeredBy.displayName}</strong> closed the
                  job as Cancelled
                </>
              )
            case 'TEST':
              return (
                <>
                  <strong>{event.triggeredBy.displayName}</strong> closed the
                  job as Test
                </>
              )
            default:
              return (
                <>
                  <strong>{event.triggeredBy.displayName}</strong> closed this
                  job
                </>
              )
          }
        },
        FORFEITED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> forfeited the job
          </>
        ),
        JOB_MEMBER_ADDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> added{' '}
            <strong>{event.previousProvider}</strong> to the job
          </>
        ),
        JOB_MEMBER_REMOVED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> removed{' '}
            <strong>{event.previousProvider}</strong> from the job
          </>
        ),
        PROVIDER_ADDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> added{' '}
            <strong>{event.newProvider}</strong> to the job
          </>
        ),
        PROVIDER_REMOVED: () => {
          if (event.previousProvider) {
            return (
              <>
                <strong>{event.triggeredBy.displayName}</strong> removed{' '}
                <strong>{event.previousProvider}</strong> from the job
              </>
            )
          }
          return (
            <>
              <strong>{event.triggeredBy.displayName}</strong>{' '}
              {event.description}
            </>
          )
        },
        REOPENED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> reopened the job
          </>
        ),
        TASK_MARKED_COMPLETE: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> marked{' '}
            <strong>"{event.taskTitle}"</strong> complete
          </>
        ),
        TASK_MARKED_INCOMPLETE: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> marked{' '}
            <strong>"{event.taskTitle}"</strong> incomplete
          </>
        ),
        TRANSFERRED: () => {
          if (event.newTeam) {
            return (
              <>
                <strong>{event.triggeredBy.displayName}</strong> transferred the
                job to team <strong>{event.newTeam}</strong>{' '}
              </>
            )
          }
          if (event.newProvider) {
            return (
              <>
                <strong>{event.triggeredBy.displayName}</strong> transferred the
                job to <strong>{event.newProvider}</strong>{' '}
              </>
            )
          }
          return (
            <>
              <strong>{event.triggeredBy.displayName}</strong> transferred the
              job to <strong>{event.newClient}</strong>{' '}
            </>
          )
        },
        UNCLAIMED: () => (
          <>
            {event.description} by{' '}
            <strong>{event.triggeredBy.displayName}</strong>
          </>
        ),
        UPDATED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> updated the job
          </>
        ),
        RESUMED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> resumed the job
          </>
        ),
        RESUMED_JOB: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> resumed the job
          </>
        ),
        PUT_ON_HOLD: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> put the job on hold
          </>
        ),
        BECAME_INACTIVE: () => (
          <>
            <strong>The job</strong> became inactive
          </>
        ),
        PAYMENT_INFO_REQUESTED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> requested payment
            information
          </>
        ),
        PAYMENT_INFO_PROVIDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> provided payment
            information
          </>
        ),
        PAYMENT_SUCCESSFUL: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> has settled the
            balance
          </>
        ),
        PAYMENT_FAILED: () => (
          <>
            <strong>Payment</strong> failed for this job
          </>
        ),
        COUPON_ADDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> added{' '}
            <strong>a coupon</strong>
          </>
        ),
        COUPON_REMOVED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> removed{' '}
            <strong>a coupon</strong>
          </>
        ),
        REFUND_SUCCESSFUL: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> refunded
            successfully
          </>
        ),
        REFUND_FAILED: () => (
          <>
            <strong>Attempt to refund</strong> failed
          </>
        ),
        MANUAL_INVOICE_ADDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> added an invoice to
            the job
          </>
        ),
        ANSWERS_CORRECTED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> corrected the
            answers for the job
          </>
        ),
        INFO_GATHERING_COMPLETED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> completed info
            gathering for the job
          </>
        ),
        CLIENT_ADDED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> added a client to
            the job
          </>
        ),
        UNSPECIFIED: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> {event.description}
          </>
        ),
        [match.DEFAULT]: () => (
          <>
            <strong>{event.triggeredBy.displayName}</strong> {event.description}
          </>
        ),
      })}
    </p>
  )
})
